import { Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/route-guards/ProtectedRoute";
import { TabGuard } from "@/components/route-guards/TabGuard";
import { SmartRedirect } from "@/components/route-guards/SmartRedirect";
import { PermissionGuard } from "@/components/route-guards/PermissionGuard";
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import Clients from "@/pages/Clients";
import ClientsArchitecture from "@/pages/ClientsArchitecture";
import People from "@/pages/People";
import Proposals from "@/pages/Proposals";
import Projects from "@/pages/Projects";
import Activities from "@/pages/Activities";
import Invoices from "@/pages/Invoices";
import XeroLite from "@/pages/XeroLite";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Initiatives from "@/pages/Initiatives";
import Wisdrop from "@/pages/Wisdrop";
import NotFound from "@/pages/NotFound";
import Unauthorized from "@/pages/Unauthorized";
import BillDetailPage from "@/components/bills/BillDetailPage";

export interface RouteConfig {
  path: string;
  element: JSX.Element;
  index?: boolean;
}

// Public routes (no authentication required)
export const publicRoutes: RouteConfig[] = [
  { 
    path: "/auth", 
    element: (
      <ProtectedRoute requireAuth={false}>
        <Auth />
      </ProtectedRoute>
    )
  },
  { path: "/unauthorized", element: <Unauthorized /> },
  { path: "*", element: <NotFound /> },
];

// Protected routes (inside Layout, require authentication and tab-based access)
export const protectedRoutes: RouteConfig[] = [
  { 
    path: "", 
    index: true, 
    element: <SmartRedirect />
  },
  { 
    path: "dashboard", 
    element: (
      <TabGuard requiredTab="dashboard">
        <Index />
      </TabGuard>
    )
  },
  { 
    path: "clients", 
    element: (
      <TabGuard requiredTab="clients">
        <Clients />
      </TabGuard>
    )
  },
  { 
    path: "clients-architecture", 
    element: (
      <TabGuard requiredTab="clients">
        <ClientsArchitecture />
      </TabGuard>
    )
  },
  { 
    path: "people", 
    element: (
      <TabGuard requiredTab="people">
        <People />
      </TabGuard>
    )
  },
  { 
    path: "proposals", 
    element: (
      <TabGuard requiredTab="proposals">
        <Proposals />
      </TabGuard>
    )
  },
  { 
    path: "projects", 
    element: (
      <TabGuard requiredTab="projects">
        <Projects />
      </TabGuard>
    )
  },
  { 
    path: "activities", 
    element: (
      <TabGuard requiredTab="activities">
        <Activities />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <Navigate to="/xero-lite/invoices" replace />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite/invoices", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <XeroLite />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite/bills", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <XeroLite />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite/bills/:id", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <BillDetailPage />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite/suppliers", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <XeroLite />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite/banking", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <XeroLite />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite/reports", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <XeroLite />
      </TabGuard>
    )
  },
  { 
    path: "xero-lite/settings", 
    element: (
      <TabGuard requiredTab="xero-lite">
        <XeroLite />
      </TabGuard>
    )
  },
  { 
    path: "users", 
    element: (
      <TabGuard requiredTab="users">
        <Users />
      </TabGuard>
    )
  },
  { 
    path: "initiatives", 
    element: (
      <TabGuard requiredTab="initiatives">
        <Initiatives />
      </TabGuard>
    )
  },
  { 
    path: "wisdrop", 
    element: (
      <TabGuard requiredTab="wisdrop">
        <Wisdrop />
      </TabGuard>
    )
  },
  { 
    path: "settings", 
    element: (
      <TabGuard requiredTab="settings">
        <Settings />
      </TabGuard>
    )
  },
];